import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useChannels,
  useFavoriteTeams,
  useScheduleSearch,
  type SearchFilters,
} from "./api";

const EMPTY_FILTERS: SearchFilters = { search: "", team: "", channel: "" };
const DEBOUNCE_MS = 300;

export type UseDebouncedScheduleSearchOptions = {
  customer: string;
  isAuthenticated: boolean;
  /** Defers the debounce timer and every query until the search panel is open. */
  enabled: boolean;
  limit?: number;
};

/**
 * Shared data wiring behind the header search panel: debounces the free-text
 * query, runs the live/vod schedule searches plus the channels and
 * favorite-teams lookups, and exposes the team/channel filter toggles.
 * Desktop and mobile search only differ in trigger and layout; this hook is
 * the single place their query behavior is defined.
 */
export function useDebouncedScheduleSearch({
  customer,
  isAuthenticated,
  enabled,
  limit = 5,
}: UseDebouncedScheduleSearchOptions) {
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const queryFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch]
  );

  const liveQuery = useScheduleSearch("live", customer, queryFilters, { limit, enabled });
  const vodQuery = useScheduleSearch("vod", customer, queryFilters, { limit, enabled });
  const channelsQuery = useChannels(customer, { enabled });
  const favoritesQuery = useFavoriteTeams({ enabled: enabled && isAuthenticated });

  const selectedTeams = useMemo(
    () => (filters.team ? filters.team.split(",") : []),
    [filters.team]
  );
  const selectedChannels = useMemo(
    () => (filters.channel ? filters.channel.split(",") : []),
    [filters.channel]
  );

  const setSearchText = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);

  const resetSearch = useCallback(() => {
    setFilters((prev) => ({ ...prev, search: "" }));
    setDebouncedSearch("");
  }, []);

  const toggleFilter = useCallback((key: "team" | "channel", id: string) => {
    setFilters((prev) => {
      const current = prev[key] ? prev[key].split(",") : [];
      const next = current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id];
      return { ...prev, [key]: next.join(",") };
    });
  }, []);

  return {
    filters,
    setSearchText,
    resetSearch,
    toggleFilter,
    selectedTeams,
    selectedChannels,
    liveQuery,
    vodQuery,
    channelsQuery,
    favoritesQuery,
  };
}
